import React, { useState, useEffect, useContext } from 'react'
import styled from 'styled-components'
import iconEmpty from 'icons/createproject/supportneed.svg'
import {
  Button,
  Form,
  Row,
  Col,
  Input,
  Select,
  Table,
  Space,
  Alert,
  Modal,
  Switch,
  Tree,
} from 'antd'
import { CounterContext } from 'redux/Golbalvalues'
import {
  PlusOutlined,
  SearchOutlined,
  RedoOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons'
import { getCookie } from 'helper/cookie'
import { toUpper } from 'lodash'
import {
  getTree,
  getBenefitTypeList,
  createBenefitType,
  updateBenefitType,
  deleteBenefitType,
} from 'api/Admin/benefit'
import lang from 'lang/en'
import FilterSearchTree from './FilterSearchTree'

const BenefitContainer = styled.div``
const EmptyContainer = styled.div``
const Container = styled.div``
const Header = styled.div``
const Body = styled.div``
const TextTitleBold = styled.h2`
  font-family: 'IBMPlexSansThaiBold';
`

// ── Initial State ──────────────────────────────────────────────────────────────
const initalState = {
  data: [],
  isLoading: true,
  pagination: {
    pageSize: 10,
    total: 0,
    current: 1,
    showQuickJumper: 1,
  },
}

let wordError = ''

const PROJECT_USAGE_BLOCK_MESSAGE =
  'Cannot delete because this item is used by a project'

const isProjectUsageBlocked = (response) =>
  response?.status === 400 &&
  (response?.data?.message === PROJECT_USAGE_BLOCK_MESSAGE ||
    response?.message === PROJECT_USAGE_BLOCK_MESSAGE)

// ── Tree helpers ───────────────────────────────────────────────────────────────
const buildTreeData = (companies, searchValue) =>
  companies.map((item) => {
    const parts = item.title.split(' ')
    const code = parts[0]
    const name = parts.slice(1).join(' ')
    const isMatch =
      searchValue && item.title.toLowerCase().includes(searchValue.toLowerCase())

    const title = (
      <span
        className="event-search-tree-container"
        style={{ display: 'flex', alignItems: 'center', marginTop: 4.5 }}
      >
        <h5 className="code-tree-style">{code}</h5>
        <h5
          className="title-cut"
          style={{
            marginLeft: 8,
            background: isMatch ? 'rgba(18,126,195,0.16)' : 'transparent',
            padding: isMatch ? '0px 4px' : 0,
            borderRadius: 16,
          }}
        >
          {name}
        </h5>
      </span>
    )
    return { ...item, title }
  })

// ── Component ──────────────────────────────────────────────────────────────────
const Benefit = () => {
  const token = getCookie('access_token_admin')
  const language = getCookie('lang')
  const word = lang[language]

  // tree
  const [treeData, setTreeData] = useState([])
  const [selectedCompanyId, setSelectedCompanyId] = useState(null)
  const [selectedKeys, setSelectedKeys] = useState([])
  const [filteredCompanyIds, setFilteredCompanyIds] = useState([])
  const [expandedKeys, setExpandedKeys] = useState([])

  // table
  const [dataBenefit, setDataBenefit] = useState(initalState.data)
  const [isLoading, setIsLoading] = useState(initalState.isLoading)
  const [pagination, setPagination] = useState(initalState.pagination)

  // drawer / modal
  const [visible, setVisible] = useState(false)
  const [stateDrawer, setStateDrawer] = useState('')
  const [titleDrawer, setTitleDrawer] = useState('')
  const [dataEdit, setDataEdit] = useState()
  const [hiddenError, setHiddenError] = useState(true)
  const [switchValue, setSwitchValue] = useState(true)
  const [disablePage, setDisablePage] = useState(false)

  // delete modal
  const [visibleDeleteModal, setVisibleDeleteModal] = useState(false)
  const [visibleCantDeleteModal, setVisibleCantDeleteModal] = useState(false)

  const [form] = Form.useForm()

  // search
  const [searchedColumn, setSearchedColumn] = useState('')
  const [searchText, setSearchText] = useState('')

  // checkrole
  const { adminPermission } = useContext(CounterContext)
  const roleUser = getCookie('roleUser')
  const [permissionPage, setPermissionPage] = useState({
    create: false,
    read: false,
    update: false,
    delete: false,
  })

  useEffect(() => {
    if (roleUser == 1) {
      setPermissionPage({ create: true, read: true, update: true, delete: true })
    } else {
      adminPermission.map((item) => {
        if (item.menu_id == 26) {
          setPermissionPage({
            create: item.create == 'Y',
            read: item.read == 'Y',
            update: item.update == 'Y',
            delete: item.delete == 'Y',
          })
        }
      })
    }
  }, [adminPermission])

  // ── Load company tree on mount ───────────────────────────────────────────────
  useEffect(() => {
    const fetchTree = async () => {
      const res = await getTree({ token })
      const level1 = (res?.data?.result?.[0]?.children || []).map(
        ({ children, ...item }) => item,
      )
      setTreeData(level1)
      if (level1.length > 0) {
        const first = level1[0]
        setSelectedKeys([first.key])
        setExpandedKeys([first.key])
        setSelectedCompanyId(first.id)
      }
    }
    fetchTree()
  }, [])

  // ── Load benefit types when company changes ──────────────────────────────────
  useEffect(() => {
    if (selectedCompanyId === null) return
    const fetchBenefitTypes = async () => {
      setIsLoading(true)
      const res = await getBenefitTypeList({ token, buId: selectedCompanyId })
      setDataBenefit(res?.data?.result || [])
      setIsLoading(false)
    }
    fetchBenefitTypes()
  }, [selectedCompanyId])

  // ── Sync filter selection → auto-select first company ────────────────────────
  useEffect(() => {
    if (treeData.length === 0) return
    if (filteredCompanyIds.length === 0 || filteredCompanyIds.includes(0)) {
      const first = treeData[0]
      setSelectedKeys([first.key])
      setSelectedCompanyId(first.id)
    } else {
      const filtered = treeData.filter((c) => filteredCompanyIds.includes(c.id))
      if (filtered.length > 0) {
        const first = filtered[0]
        setSelectedKeys([first.key])
        setSelectedCompanyId(first.id)
      }
    }
  }, [filteredCompanyIds])

  // ── Tree handlers ─────────────────────────────────────────────────────────────
  const onTreeSelect = (keys, info) => {
    if (!keys.length) return
    setSelectedKeys(keys)
    setSelectedCompanyId(info.node.id)
  }

  const onTreeExpand = (keys) => {
    setExpandedKeys(keys)
  }

  // ── Table ─────────────────────────────────────────────────────────────────────
  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            className="mini-search-btn-admin"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          >
            <SearchOutlined className="icon" />
            Search
          </Button>
          <Button
            type="primary"
            className="mini-search-btn-admin-reset"
            onClick={() => handleReset(clearFilters)}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : '',
    render: (text) => (searchedColumn === dataIndex ? text : text),
  })

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm()
    setSearchText(selectedKeys[0])
    setSearchedColumn(dataIndex)
  }

  const handleReset = (clearFilters) => {
    clearFilters()
    setSearchText('')
  }

  const selectedCompany = treeData.find((c) => c.id === selectedCompanyId)

  const dataSource = dataBenefit.map((item) => ({
    id: item.id,
    company_id: selectedCompanyId,
    business: selectedCompany ? selectedCompany.title.split(' ')[0] : '',
    benefit_code: item.code,
    local_name: item.name,
    english_name: item.name_en,
    status: item.status,
    data: item,
  }))

  const columns = [
    {
      title: <h5>Business</h5>,
      dataIndex: 'business',
      ellipsis: true,
    },
    {
      title: <h5>Local Benefit Type Name</h5>,
      dataIndex: 'local_name',
      sorter: (a, b) => a.local_name.localeCompare(b.local_name),
      ellipsis: true,
      ...getColumnSearchProps('local_name'),
    },
    {
      title: <h5>English Benefit Type Name</h5>,
      dataIndex: 'english_name',
      sorter: (a, b) => a.english_name.localeCompare(b.english_name),
      ellipsis: true,
      ...getColumnSearchProps('english_name'),
    },
    {
      title: <h5>Status</h5>,
      dataIndex: 'status',
      width: 180,
      render: (_, record) => <span>{record.data.status}</span>,
      sorter: (a, b) => a.status.localeCompare(b.status),
      ellipsis: true,
      ...getColumnSearchProps('status'),
    },
  ]

  function onChange(pagination2) {
    if (pagination !== false) {
      setPagination((prevState) => ({
        ...prevState,
        pageSize: pagination2.pageSize,
        current: pagination2.current,
      }))
    }
  }

  // ── CRUD ──────────────────────────────────────────────────────────────────────
  const eventAdd = () => {
    form.resetFields()
    setSwitchValue(true)
    form.setFieldsValue({
      company_code: selectedCompanyId ?? null,
      bn_code: null,
      localName: null,
      engName: null,
      status: true,
    })
    setHiddenError(true)
    setVisible(true)
    setStateDrawer('add')
    setTitleDrawer('Add Benefit Type')
  }

  const eventEdit = (data) => {
    permissionPage.update ? setDisablePage(false) : setDisablePage(true)
    const isActive = data.data.status === 'Active'
    setSwitchValue(isActive)
    form.setFieldsValue({
      company_code: data.company_id,
      bn_code: data.benefit_code,
      localName: data.local_name,
      engName: data.english_name,
      status: isActive,
    })
    setHiddenError(true)
    setDataEdit(data)
    setStateDrawer('edit')
    setTitleDrawer('Edit Benefit Type')
    setVisible(true)
  }

  const onFinish = async (value) => {
    if (stateDrawer === 'add') {
      const payload = {
        name: value.localName,
        name_en: value.engName,
        company_id: value.company_code,
        status: value.status ? 'Active' : 'InActive',
      }
      const res = await createBenefitType({ token, payload })
      if (res?.data?.status === 200 || res?.status === 200) {
        setVisible(false)
        setSwitchValue(true)
        const refetch = await getBenefitTypeList({ token, buId: selectedCompanyId })
        setDataBenefit(refetch?.data?.result || [])
      }
    } else {
      const payload = {
        name: value.localName,
        name_en: value.engName,
        company_id: value.company_code,
        status: value.status ? 'Active' : 'InActive',
      }
      const res = await updateBenefitType({ token, id: dataEdit.id, payload })
      if (res?.data?.status === 200 || res?.status === 200) {
        setVisible(false)
        setSwitchValue(true)
        const refetch = await getBenefitTypeList({ token, buId: selectedCompanyId })
        setDataBenefit(refetch?.data?.result || [])
      }
    }
  }

  const onDelete = () => {
    setVisibleDeleteModal(true)
  }

  const renderTable = () => {
    return (
      <Table
        key="benefit_table"
        loading={isLoading}
        onRow={(record) => ({ onClick: () => eventEdit(record) })}
        columns={columns}
        dataSource={dataSource}
        onChange={onChange}
        pagination={pagination}
        showSorterTooltip={false}
      />
    )
  }

  const onClose = () => setVisible(false)

  const renderButton_Drawer = () => {
    if (stateDrawer === 'add') {
      return (
        <div style={{ textAlign: 'right' }}>
          <Button className="BTN_Next" onClick={form.submit}>
            {word['Save']}
          </Button>
        </div>
      )
    } else {
      return (
        <div style={{ textAlign: 'right' }}>
          <Button
            className="BTN_Delete_CPF_RED"
            onClick={onDelete}
            hidden={permissionPage.delete ? false : true}
            style={{ marginRight: 8 }}
          >
            {word['Delete']}
          </Button>
          <Button
            hidden={permissionPage.update ? false : true}
            className="BTN_Next"
            onClick={form.submit}
            type="primary"
          >
            {word['Save']}
          </Button>
        </div>
      )
    }
  }

  // ── Content (right panel) ─────────────────────────────────────────────────────
  const content = () => {
    if (dataBenefit.length > 0) {
      return (
        <Container>
          <Header className="layout-button-add-normal-state">
            <div className="layout-btn-reset-add">
              <Button
                id="add"
                onClick={eventAdd}
                icon={<PlusOutlined className="Icon-color" />}
                className="BTN_Next"
                hidden={permissionPage.create ? false : true}
              >
                Add Benefit Type
              </Button>
              <Button
                id="reset"
                type="primary"
                style={{ marginLeft: 8 }}
                className="btn_Secondary2"
                onClick={() => window.location.reload()}
              >
                <RedoOutlined className="Icon-color" />
                {word['Reset']}
              </Button>
            </div>
          </Header>
          <Body>{renderTable()}</Body>
        </Container>
      )
    } else {
      return (
        <EmptyContainer className="initialstate-container">
          <img src={iconEmpty} />
          <h2>Please add Benefit Type</h2>
          <Button
            hidden={permissionPage.create ? false : true}
            id="add"
            onClick={eventAdd}
            icon={<PlusOutlined className="Icon-color" />}
            className="btn_Secondary2"
          >
            Add Benefit Type
          </Button>
        </EmptyContainer>
      )
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <BenefitContainer>
      <div className="layoutmanagement-git-master">
        {/* ── Left: Company Tree ── */}
        <div className="layout-left">
          <div
            style={{ marginTop: 16, marginBottom: 16, marginLeft: 16, marginRight: 16 }}
          >
            <FilterSearchTree
              dataItems={treeData.map((item) => ({
                ...item,
                name_en: item.title.split(' ').slice(1).join(' '),
              }))}
              selectedItems={filteredCompanyIds}
              setSelectedItems={setFilteredCompanyIds}
              itemTypeName="Business"
              treeId="benefit-company"
            />
          </div>
          <Tree
            treeData={buildTreeData(
              treeData.filter(
                (c) =>
                  filteredCompanyIds.length === 0 ||
                  filteredCompanyIds.includes(0) ||
                  filteredCompanyIds.includes(c.id),
              ),
              '',
            )}
            selectedKeys={selectedKeys}
            expandedKeys={expandedKeys}
            onExpand={onTreeExpand}
            onSelect={onTreeSelect}
            blockNode
          />
        </div>

        {/* ── Right: Table ── */}
        <div className="layout-right">{content()}</div>
      </div>

      {/* ── Modal: Add / Edit ── */}
      <Modal
        maskClosable={false}
        title={<TextTitleBold>{titleDrawer}</TextTitleBold>}
        placement="right"
        onCancel={onClose}
        visible={visible}
        width="634px"
        footer={renderButton_Drawer()}
        destroyOnClose
        className="fix-modal"
        centered
      >
        <Form className="disable-page" form={form} layout="vertical" onFinish={onFinish}>
          <fieldset disabled={disablePage}>
            <div hidden={hiddenError} style={{ marginBottom: 10 }}>
              <Alert message={wordError} type="error" showIcon />
            </div>
            {/* Row 1: Business Code */}
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  id="company_code"
                  name="company_code"
                  label={<h4>Business Code</h4>}
                  rules={[
                    {
                      required: true,
                      message: 'Please select business',
                    },
                  ]}
                >
                  <Select
                    showSearch
                    id="select"
                    size="large"
                    placeholder="Please select business"
                    allowClear
                    optionFilterProp="children"
                    disabled={disablePage}
                  >
                    {treeData.map((item) => (
                      <Select.Option key={item.id} value={item.id}>
                        {item.title}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            {/* Row 2: Local Benefit Type Name */}
            <Form.Item
              id="localName"
              name="localName"
              label={<h4>Local Benefit Type Name</h4>}
              rules={[
                { whitespace: true, required: true, message: 'Please enter local name' },
              ]}
            >
              <Input id="localName" size="large" placeholder="Please enter local name" />
            </Form.Item>
            {/* Row 3: English Benefit Type Name */}
            <Form.Item
              id="engName"
              name="engName"
              label={<h4>English Benefit Type Name</h4>}
              rules={[
                {
                  whitespace: true,
                  required: true,
                  message: 'Please enter english name',
                },
              ]}
            >
              <Input id="engName" size="large" placeholder="Please enter english name" />
            </Form.Item>
            {/* Row 4: Status */}
            <Form.Item id="status" name="status" valuePropName="checked">
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ marginRight: '8px' }}>Status Active:</span>
                <Switch
                  checked={switchValue}
                  onChange={(checked) => {
                    form.setFieldsValue({ status: checked })
                    setSwitchValue(checked)
                  }}
                />
              </div>
            </Form.Item>
          </fieldset>
        </Form>
        {/* ── Modal: Confirm Delete ── */}
        <Modal
          maskClosable={false}
          className="DeleteModal"
          visible={visibleDeleteModal}
          okText={word['Delete']}
          okButtonProps={{ danger: true }}
          cancelText={word['Cancel']}
          onOk={async () => {
            const res = await deleteBenefitType({ token, id: dataEdit.id })
            if (res?.data?.status === 200 || res?.status === 200) {
              setVisibleDeleteModal(false)
              setVisible(false)
              const refetch = await getBenefitTypeList({ token, buId: selectedCompanyId })
              setDataBenefit(refetch?.data?.result || [])
            } else if (isProjectUsageBlocked(res)) {
              setVisibleDeleteModal(false)
              setVisibleCantDeleteModal(true)
            }
          }}
          onCancel={() => setVisibleDeleteModal(false)}
        >
          <CloseCircleOutlined className="CloseCircleOutlined" />
          <h2>Delete Benefit Type</h2>
          <h4>{`Are you sure you want to delete Benefit Type `}</h4>
        </Modal>
      </Modal>

      <Modal
        maskClosable={false}
        className="DeleteModal edit-size-modal-delete"
        visible={visibleCantDeleteModal}
        onCancel={() => setVisibleCantDeleteModal(false)}
        okButtonProps={{ hidden: true }}
        cancelButtonProps={{ hidden: true }}
      >
        <CloseCircleOutlined className="CloseCircleOutlined" />
        <h2>{word.cant_delete_benefit_type_title}</h2>
        <h4>{word.cant_delete_benefit_type_des}</h4>
      </Modal>
    </BenefitContainer>
  )
}

export default Benefit
